// ---------------------------------------------------------------------------
// Signers constants
// ---------------------------------------------------------------------------
import { t } from "i18next";

import type { Tier } from "../../../../components/ui/tierBadge";

export const MAX_INCOMES_PER_PERSON = 3;

// Option labels are resolved here via t() (English-only app; i18n is
// initialized before this module evaluates — see src/i18n.ts / setupTests.ts).
// `value` is the canonical EMPLOYMENT_TYPES code (matches CAPS/dealer-ui) so the
// form speaks the same language as the BFF. Copy lives in src/languages/en.json.

// ---------------------------------------------------------------------------
// Income Type options
// ---------------------------------------------------------------------------
export const INCOME_TYPE_OPTIONS = [
  { value: "6", label: t("review.options.incomeType.employee") },
  { value: "7", label: t("review.options.incomeType.selfEmployed") },
  { value: "8", label: t("review.options.incomeType.fixedIncome") },
  { value: "9", label: t("review.options.incomeType.tempServiceEmployee") },
  { value: "10", label: t("review.options.incomeType.unemployed") },
  { value: "11", label: t("review.options.incomeType.cashIncome") },
] as const;

export type IncomeTypeOption = (typeof INCOME_TYPE_OPTIONS)[number];

// Maps each income-type code to the BFF's `incomeType` enum value.
export const INCOME_TYPE_CODE_TO_ENUM: Record<IncomeTypeOption["value"], string> = {
  "6": "W2",
  "7": "Self-Employed",
  "8": "Fixed Income",
  "9": "Temp Service",
  "10": "Unemployed",
  "11": "Cash Income",
};

// ---------------------------------------------------------------------------
// Income Type filtering by applicant SSN/ITIN status
// ---------------------------------------------------------------------------
export const APPLICANT_SSN_TYPES = {
  SSN: "ssn",
  NO_ITIN_SSN: "noItinSsn",
  ITIN: "itin",
} as const;

export type ApplicantSsnType = (typeof APPLICANT_SSN_TYPES)[keyof typeof APPLICANT_SSN_TYPES];

// Income-type codes a non-SSN applicant may select: Self-Employed (7),
// Unemployed (10), Cash Income (11).
const NON_SSN_INCOME_TYPE_CODES = new Set<IncomeTypeOption["value"]>(["7", "10", "11"]);

// Income-type codes with no employer: Fixed Income (Net) (8), Unemployed (10).
// Employer Name and Employment Verification Phone are not required for these (OR-23412).
export const EMPLOYER_OPTIONAL_INCOME_TYPE_CODES = new Set<IncomeTypeOption["value"]>(["8", "10"]);

// Income-type codes that collect Occupation on the Call Script modal:
// Self-Employed (7), Cash Income (11).
export const OCCUPATION_INCOME_TYPE_CODES = new Set<string>(["7", "11"]);

// Employee (Gets W2) income-type code. Pull Purchased Employer Info (worknumber
// eligibility/history/pull) only applies to W2 income (OR-23450).
export const W2_INCOME_TYPE_CODE: IncomeTypeOption["value"] = "6";

/**
 * Income-type options filtered by the applicant's SSN/ITIN status, mirroring
 * dealer-ui's `getIncomeTypeOptions`: an applicant without a validated SSN
 * (no-SSN/ITIN or ITIN) may only pick Self-Employed, Unemployed, or Cash
 * Income; an SSN applicant gets the full list.
 *
 * `applicantSsnType` is derived from the dashboard `residence` section's
 * `isNoItinSsn` / `isSsn` flags (see `deriveApplicantSsnType` in
 * residenceMapping.ts, step6-bff-client@1.0.287) — an unknown/ITIN applicant
 * falls to the restricted list, matching dealer-ui.
 * @param {ApplicantSsnType} applicantSsnType - The applicant's SSN/ITIN status.
 * @returns {IncomeTypeOption[]} The income-type options allowed for that status.
 */
export function getIncomeTypeOptions(applicantSsnType: ApplicantSsnType): IncomeTypeOption[] {
  if (applicantSsnType === APPLICANT_SSN_TYPES.SSN) {
    return [...INCOME_TYPE_OPTIONS];
  }
  return INCOME_TYPE_OPTIONS.filter((option) => NON_SSN_INCOME_TYPE_CODES.has(option.value));
}

/**
 * Whether an income-type code is selectable for the given SSN/ITIN status.
 * Used to clamp a hydrated income type that is no longer valid for a non-SSN
 * applicant (see incomeMapping).
 * @param {string} value - The income-type code (EMPLOYMENT_TYPES id).
 * @param {ApplicantSsnType} applicantSsnType - The applicant's SSN/ITIN status.
 * @returns {boolean} True when the code is allowed for that status.
 */
export function isIncomeTypeAllowed(value: string, applicantSsnType: ApplicantSsnType): boolean {
  return getIncomeTypeOptions(applicantSsnType).some((option) => option.value === value);
}

/**
 * The default income-type code for a freshly seeded income block, honoring the
 * applicant's SSN/ITIN status: the first allowed option (Employee "6" for an
 * SSN applicant, Self-Employed "7" for a non-SSN one).
 * @param {ApplicantSsnType} applicantSsnType - The applicant's SSN/ITIN status.
 * @returns {IncomeTypeOption["value"]} The default income-type code.
 */
export function getDefaultIncomeType(applicantSsnType: ApplicantSsnType): IncomeTypeOption["value"] {
  return getIncomeTypeOptions(applicantSsnType)[0]?.value ?? "6";
}

// ---------------------------------------------------------------------------
// Pay Frequency options (values match the dealer-ui PAY_FREQUENCY literals)
// ---------------------------------------------------------------------------
export const PAY_FREQUENCY_OPTIONS = [
  { value: "Weekly", label: t("review.options.payFrequency.weekly") },
  { value: "Bi-Weekly", label: t("review.options.payFrequency.biWeekly") },
  { value: "2x-Monthly", label: t("review.options.payFrequency.twiceMonthly") },
  { value: "Monthly", label: t("review.options.payFrequency.monthly") },
  { value: "Annual", label: t("review.options.payFrequency.annual") },
] as const;

// ---------------------------------------------------------------------------
// Gross / Net options (values match the dealer-ui PAY_TYPE literals)
// ---------------------------------------------------------------------------
export const GROSS_NET_OPTIONS = [
  { value: "Gross", label: t("review.options.grossNet.gross") },
  { value: "Net", label: t("review.options.grossNet.net") },
] as const;

export type GrossNetOption = (typeof GROSS_NET_OPTIONS)[number]["value"];

// Fixed Income (Net) income-type code — see EMPLOYER_OPTIONAL_INCOME_TYPE_CODES
// above. CAPS/dealer-ui has no separate Gross/Net choice for this income type
// (the type itself is "Fixed Income (Net)"); the Workbench exposes the
// Gross/Net dropdown for every income type, so this is the one code that must
// default to "Net" instead of "Gross" (OR-23629).
const FIXED_INCOME_NET_TYPE_CODE: IncomeTypeOption["value"] = "8";

/**
 * The default Gross/Net value for a given income-type code. Fixed Income (8)
 * defaults to "Net" (it has no employer/Gross basis in CAPS — see
 * EMPLOYER_OPTIONAL_INCOME_TYPE_CODES); every other income type defaults to
 * "Gross".
 * @param {IncomeTypeOption["value"]} incomeType - The income-type code (EMPLOYMENT_TYPES id).
 * @returns {GrossNetOption} The default Gross/Net value for that income type.
 */
export function getDefaultGrossNet(incomeType: IncomeTypeOption["value"]): GrossNetOption {
  return incomeType === FIXED_INCOME_NET_TYPE_CODE ? "Net" : "Gross";
}

// ---------------------------------------------------------------------------
// US States — static list (BFF/dynamic replacement in a later story).
// Labelled by abbreviation (label === value) so the select shows "MI", not
// "Michigan" — matches how State is captured/displayed elsewhere.
// ---------------------------------------------------------------------------
export const US_STATE_OPTIONS = [
  { value: "AL", label: "AL" },
  { value: "AK", label: "AK" },
  { value: "AZ", label: "AZ" },
  { value: "AR", label: "AR" },
  { value: "CA", label: "CA" },
  { value: "CO", label: "CO" },
  { value: "CT", label: "CT" },
  { value: "DE", label: "DE" },
  { value: "FL", label: "FL" },
  { value: "GA", label: "GA" },
  { value: "HI", label: "HI" },
  { value: "ID", label: "ID" },
  { value: "IL", label: "IL" },
  { value: "IN", label: "IN" },
  { value: "IA", label: "IA" },
  { value: "KS", label: "KS" },
  { value: "KY", label: "KY" },
  { value: "LA", label: "LA" },
  { value: "ME", label: "ME" },
  { value: "MD", label: "MD" },
  { value: "MA", label: "MA" },
  { value: "MI", label: "MI" },
  { value: "MN", label: "MN" },
  { value: "MS", label: "MS" },
  { value: "MO", label: "MO" },
  { value: "MT", label: "MT" },
  { value: "NE", label: "NE" },
  { value: "NV", label: "NV" },
  { value: "NH", label: "NH" },
  { value: "NJ", label: "NJ" },
  { value: "NM", label: "NM" },
  { value: "NY", label: "NY" },
  { value: "NC", label: "NC" },
  { value: "ND", label: "ND" },
  { value: "OH", label: "OH" },
  { value: "OK", label: "OK" },
  { value: "OR", label: "OR" },
  { value: "PA", label: "PA" },
  { value: "RI", label: "RI" },
  { value: "SC", label: "SC" },
  { value: "SD", label: "SD" },
  { value: "TN", label: "TN" },
  { value: "TX", label: "TX" },
  { value: "UT", label: "UT" },
  { value: "VT", label: "VT" },
  { value: "VA", label: "VA" },
  { value: "WA", label: "WA" },
  { value: "WV", label: "WV" },
  { value: "WI", label: "WI" },
  { value: "WY", label: "WY" },
  { value: "DC", label: "DC" },
] as const;

// ---------------------------------------------------------------------------
// Placeholder city list — static (BFF/dynamic replacement in a later story)
// ---------------------------------------------------------------------------
// City options are sourced from real data at runtime; no sample/seed values here.
export const CITY_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [];

// ---------------------------------------------------------------------------
// Ordinal labels for income block headers
// ---------------------------------------------------------------------------
export const INCOME_ORDINAL_LABELS = ["1st", "2nd", "3rd"] as const;

// ---------------------------------------------------------------------------
// Bank Information constants
// ---------------------------------------------------------------------------

// Letters, spaces, forward-slash, and question-mark only (mirrors dealer-ui).
export const BANK_NAME_REGEX = /^[\s/?A-Za-z]*$/;

export const BANK_NAME_MAX_LENGTH = 100;

// Account-type checkbox option list: field-key + i18n-resolved label.
// Order matches dealer-ui / Figma: Savings → Checking → Prepaid Debit Card.
export const ACCOUNT_TYPE_OPTIONS = [
  { field: "isSavingsAccount" as const, label: t("review.bank.accountType.savings") },
  { field: "isCheckingAccount" as const, label: t("review.bank.accountType.checking") },
  { field: "isPrepaidDebit" as const, label: t("review.bank.accountType.prepaidDebit") },
] as const;

// Scoring-program tiers for which Bank Name stays optional even when an
// account type is checked (OR-23394 — dealer-ui parity).
const BANK_NAME_OPTIONAL_TIERS = new Set<Tier>(["GOLD", "PLATINUM"]);

/**
 * Whether Bank Name validation is optional for the given deal: true for
 * GOLD/PLATINUM tiers, or when the deal is still a pre-approval app.
 * @param {Tier | undefined} tier - The deal's scoring-program tier.
 * @param {boolean} isPreApprovalApp - True when the deal is a pre-approval app.
 * @returns {boolean} True when Bank Name is optional.
 */
export function isBankNameOptionalForTier(tier: Tier | undefined, isPreApprovalApp: boolean): boolean {
  return isPreApprovalApp || (tier !== undefined && BANK_NAME_OPTIONAL_TIERS.has(tier));
}

// ---------------------------------------------------------------------------
// Customer Information constants (OR-21996)
// ---------------------------------------------------------------------------

export const MAX_PHONES_PER_PERSON = 5;

export const PHONE_EXTENSION_MAX_LENGTH = 4;

export const PHONE_NUMBER_DIGITS = 10;

// ZIP — supports plain 5-digit or ZIP+4 (#####-####); base is always capped at
// 5 digits and the +4 extension at 4 digits.
export const ZIP_BASE_LENGTH = 5;
export const ZIP_DIGITS_MAX_LENGTH = 9;
// Native maxLength truncates a raw pasted string before formatZip strips
// non-digits, so it must cover punctuated pastes (e.g. "90210-1234 extra"),
// not just the "#####-####" display length.
export const ZIP_PASTE_MAX_LENGTH = 20;

// Address text fields (Street No./Apt. share a length; Street Name is longer).
export const ADDRESS_STREET_NUMBER_MAX_LENGTH = 10;
export const ADDRESS_STREET_NAME_MAX_LENGTH = 30;
export const ADDRESS_APARTMENT_NUMBER_MAX_LENGTH = 10;

// Income block — Employer Name / Occupation
export const EMPLOYER_NAME_MAX_LENGTH = 30;
export const OCCUPATION_MAX_LENGTH = 30;

/**
 * Formats a raw ZIP value into #### or #####-#### as digits accrue, capping the
 * base at 5 digits and the +4 extension at 4 digits. A hyphen the user types
 * themselves right after the 5th digit is kept immediately, rather than being
 * silently discarded until a 6th digit arrives.
 * @param {string} raw - The raw typed/pasted value.
 * @returns {string} The formatted ZIP.
 */
export function formatZip(raw: string): string {
  const digits = raw.replaceAll(/\D/g, "").slice(0, ZIP_DIGITS_MAX_LENGTH);
  if (digits.length < ZIP_BASE_LENGTH) {
    return digits;
  }
  if (digits.length === ZIP_BASE_LENGTH) {
    return /^\d{5}[‐-―-]/.test(raw) ? `${digits}-` : digits;
  }
  return `${digits.slice(0, ZIP_BASE_LENGTH)}-${digits.slice(ZIP_BASE_LENGTH)}`;
}

// Phone type select options (Personal/Work)
export const PHONE_TYPE_OPTIONS = [
  { value: "Personal", label: t("review.customer.options.phoneType.personal") },
  { value: "Work", label: t("review.customer.options.phoneType.work") },
] as const;

// Name suffix select options — matches dealer-ui NAME_SUFFIX_OPTION_VALUES.
// Empty string "" is the "None" placeholder value (no suffix selected).
export const SUFFIX_OPTIONS = [
  { value: "", label: t("review.customer.options.suffix.none") },
  { value: "I", label: "I" },
  { value: "II", label: "II" },
  { value: "III", label: "III" },
  { value: "IV", label: "IV" },
  { value: "Sr.", label: "Sr." },
  { value: "Jr.", label: "Jr." },
] as const;
